/**
 * ExploreDrlpsModal - Modal for exploring DealShaq partner stores
 * 
 * Uses shared DACSAI map utilities for consistent styling and behavior.
 * Displays stores inside/outside radius with the same color coding as DacsaiMap.
 */

import { useState, useCallback, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Slider } from '../ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea } from '../ui/scroll-area';
import { MapPin, Search, X, Store, Check, Navigation } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../utils/api';

// Import shared DACSAI map utilities
import {
  createStoreIcon,
  userLocationIcon,
  getZoomLevel,
  COLORS,
  sortStoresByPriority
} from '../../utils/mapUtils';

// Map updater component - syncs map view with center/radius
function MapUpdater({ center, radius }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && center.lat && center.lng) {
      map.setView([center.lat, center.lng], getZoomLevel(radius));
    }
  }, [center, radius, map]);
  
  return null;
}

export default function ExploreDrlpsModal({ isOpen, onClose, onStoreSelect }) {
  const [address, setAddress] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [radiusMiles, setRadiusMiles] = useState(3);
  const [isSearching, setIsSearching] = useState(false);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null); // null = show all, 'green', 'yellow'
  
  // Geocode address to coordinates using OpenStreetMap Nominatim
  const geocodeAddress = useCallback(async () => {
    if (!address.trim()) {
      toast.error('Please enter an address');
      return;
    }
    
    setIsGeocoding(true);
    
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const result = data[0];
        setCoordinates({
          lat: parseFloat(result.lat),
          lng: parseFloat(result.lon)
        });
        setAddress(result.display_name);
        toast.success('Address found!');
      } else {
        toast.error('Could not find that address. Please try a more specific address.');
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      toast.error('Error looking up address');
    } finally {
      setIsGeocoding(false);
    }
  }, [address]);
  
  // Search for DRLPs near the coordinates using backend API
  const searchDrlps = useCallback(async () => {
    if (!coordinates) {
      toast.error('Please enter and verify an address first');
      return;
    }
    
    setIsSearching(true);
    
    try {
      const response = await api.post('/dac/explore-nearby-drlps', {
        address: address,
        coordinates: coordinates,
        radius_miles: radiusMiles
      });
      
      // Backend returns stores already categorized as inside/outside
      // Add markerType for consistent rendering
      const storesWithMarkers = {
        ...response.data,
        inside_radius: response.data.inside_radius.map(s => ({
          ...s,
          markerType: 'drlp-inside',
          isInsideRadius: true
        })),
        outside_radius: response.data.outside_radius.map(s => ({
          ...s,
          markerType: 'drlp-outside',
          isInsideRadius: false
        }))
      };
      
      setSearchResults(storesWithMarkers);
      
      if (response.data.total_inside === 0) {
        toast.info('No DealShaq partner stores found in this area. Try increasing the radius.');
      } else {
        toast.success(response.data.summary);
      }
    } catch (err) {
      console.error('Search error:', err);
      toast.error('Error searching for stores');
    } finally {
      setIsSearching(false);
    }
  }, [coordinates, address, radiusMiles]);
  
  // Auto-search when coordinates or radius changes
  useEffect(() => {
    if (coordinates) {
      searchDrlps();
    }
  }, [coordinates, radiusMiles]);
  
  // Handle filter toggle (same as DacsaiMap)
  const handleFilterClick = (filterType) => {
    setActiveFilter(activeFilter === filterType ? null : filterType);
  };
  
  if (!isOpen) return null;
  
  // Combine and sort stores using shared utility
  const allStores = sortStoresByPriority([
    ...(searchResults?.inside_radius || []),
    ...(searchResults?.outside_radius || [])
  ]);
  
  // Apply filter (same logic as DacsaiMap)
  const filteredStores = activeFilter === null 
    ? allStores 
    : allStores.filter(store => {
        if (activeFilter === 'green') return store.markerType === 'drlp-inside';
        if (activeFilter === 'yellow') return store.markerType === 'drlp-outside';
        return true;
      });
  
  // Counts for legend
  const insideCount = searchResults?.total_inside || 0;
  const outsideCount = searchResults?.total_outside || 0;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="explore-drlps-modal">
      <Card className="w-full max-w-4xl max-h-[90vh] flex flex-col bg-white">
        <CardHeader className="flex flex-row items-center justify-between border-b pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <MapPin className="w-5 h-5 text-green-600" />
            Explore DealShaq Partner Stores
          </CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="close-explore-modal">
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="flex-1 p-4 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 h-full">
            {/* Left Panel - Address Input and Store List */}
            <div className="flex flex-col gap-4">
              {/* Address Input */}
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <label className="text-sm font-medium text-gray-700">
                  Delivery Location Address
                </label>
                <div className="flex gap-2">
                  <Input
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="Enter your address..."
                    className="flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && geocodeAddress()}
                    data-testid="explore-address-input"
                  />
                  <Button
                    onClick={geocodeAddress}
                    disabled={isGeocoding}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="verify-address-btn"
                  >
                    {isGeocoding ? (
                      <Navigation className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                {/* Radius Slider - Same range as DACSAI (0.1-9.9 miles) */}
                <div className="space-y-2 pt-2">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">
                      Search Radius
                    </label>
                    <span className="text-sm font-bold text-emerald-600">
                      {radiusMiles.toFixed(1)} mi
                    </span>
                  </div>
                  <Slider
                    value={[radiusMiles]}
                    onValueChange={(value) => setRadiusMiles(value[0])}
                    min={0.1}
                    max={9.9}
                    step={0.1}
                    className="w-full"
                    data-testid="radius-slider"
                  />
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>0.1 mi</span>
                    <span>9.9 mi</span>
                  </div>
                </div>
              </div>
              
              {/* Store Legend - Clickable filters (same as DacsaiMap) */}
              {searchResults && (
                <div className="bg-white rounded-lg border border-gray-200 p-3">
                  <div className="font-semibold text-gray-800 mb-2 flex items-center gap-1 text-sm">
                    <Store className="w-4 h-4" />
                    Store Legend (Click to filter)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {/* Green - Inside radius */}
                    <button
                      onClick={() => handleFilterClick('green')}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-all ${
                        activeFilter === 'green' 
                          ? 'bg-green-500 text-white ring-2 ring-green-300' 
                          : 'bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                      data-testid="filter-green-btn"
                    >
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.DRLP_INSIDE.fill }}></span>
                      Inside ({insideCount})
                    </button>
                    
                    {/* Yellow - Outside radius */}
                    <button
                      onClick={() => handleFilterClick('yellow')}
                      className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-all ${
                        activeFilter === 'yellow' 
                          ? 'bg-yellow-500 text-white ring-2 ring-yellow-300' 
                          : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                      }`}
                      data-testid="filter-yellow-btn"
                    >
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.DRLP_OUTSIDE.fill }}></span>
                      Outside ({outsideCount})
                    </button>
                    
                    {/* Reset filter */}
                    {activeFilter && (
                      <button
                        onClick={() => setActiveFilter(null)}
                        className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-600 hover:bg-gray-200"
                        data-testid="filter-reset-btn"
                      >
                        Show All
                      </button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Store List */}
              {searchResults && (
                <div className="flex-1 min-h-0">
                  <ScrollArea className="h-[280px] border rounded-lg">
                    {filteredStores.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">
                        <Store className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p className="text-sm">No stores found</p>
                      </div>
                    ) : (
                      <div className="p-2 space-y-2">
                        {filteredStores.map((store) => (
                          <div
                            key={store.id}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              selectedStore?.id === store.id
                                ? 'ring-2 ring-blue-500'
                                : ''
                            } ${
                              store.isInsideRadius
                                ? `${COLORS.DRLP_INSIDE.bgLight} ${COLORS.DRLP_INSIDE.borderClass} hover:border-green-400`
                                : `${COLORS.DRLP_OUTSIDE.bgLight} ${COLORS.DRLP_OUTSIDE.borderClass} hover:border-yellow-400`
                            }`}
                            onClick={() => setSelectedStore(store)}
                            data-testid={`store-item-${store.id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 text-sm truncate">
                                  {store.name}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {store.distance_miles} miles away
                                  {store.has_inventory && (
                                    <span className="ml-2 text-green-600">
                                      • {store.deal_count} deals
                                    </span>
                                  )}
                                </p>
                                <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded ${
                                  store.isInsideRadius 
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {store.isInsideRadius ? 'Inside radius' : 'Outside radius'}
                                </span>
                              </div>
                              {onStoreSelect && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={`text-xs h-7 ${
                                    store.isInsideRadius
                                      ? 'border-green-500 text-green-600 hover:bg-green-100'
                                      : 'border-yellow-500 text-yellow-600 hover:bg-yellow-100'
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onStoreSelect(store);
                                  }}
                                  data-testid={`add-store-btn-${store.id}`}
                                >
                                  <Check className="w-3 h-3 mr-1" /> Add
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>
            
            {/* Right Panel - Map */}
            <div className="h-[450px] lg:h-full rounded-lg overflow-hidden border bg-gray-100">
              {coordinates ? (
                <MapContainer
                  center={[coordinates.lat, coordinates.lng]}
                  zoom={getZoomLevel(radiusMiles)}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapUpdater center={coordinates} radius={radiusMiles} />
                  
                  {/* Radius circle - using DACSAI standard colors */}
                  <Circle
                    center={[coordinates.lat, coordinates.lng]}
                    radius={radiusMiles * 1609.34} // Convert miles to meters
                    pathOptions={{
                      color: COLORS.RADIUS.stroke,
                      fillColor: COLORS.RADIUS.fill,
                      fillOpacity: COLORS.RADIUS.fillOpacity,
                      weight: 2
                    }}
                  />
                  
                  {/* Center marker - using shared userLocationIcon */}
                  <Marker
                    position={[coordinates.lat, coordinates.lng]}
                    icon={userLocationIcon}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold">Your Location</p>
                        <p className="text-gray-500 text-xs max-w-[200px] truncate">{address}</p>
                      </div>
                    </Popup>
                  </Marker>
                  
                  {/* Store markers - using shared createStoreIcon */}
                  {filteredStores.map((store) => (
                    <Marker
                      key={store.id}
                      position={[store.coordinates.lat, store.coordinates.lng]}
                      icon={createStoreIcon(store.markerType, store.has_inventory)}
                      eventHandlers={{
                        click: () => setSelectedStore(store)
                      }}
                    >
                      <Popup>
                        <div className="text-sm min-w-[150px]">
                          <p className="font-semibold">{store.name}</p>
                          <p className="text-gray-500 text-xs">
                            {store.distance_miles} miles • {store.deal_count || 0} deals
                          </p>
                          <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded ${
                            store.isInsideRadius 
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {store.isInsideRadius ? 'Inside radius' : 'Outside radius'}
                          </span>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-gray-400">
                  <div className="text-center">
                    <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p className="text-sm">Enter an address to see the map</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </CardContent>
        
        {/* Footer - Legend explanation (same as DacsaiMap) */}
        <div className="border-t p-4 flex justify-between items-center">
          <div className="text-xs text-gray-500 flex items-center gap-4">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.DRLP_INSIDE.fill }}></span>
              DealShaq Partner (inside radius)
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.DRLP_OUTSIDE.fill }}></span>
              DealShaq Partner (outside radius)
            </span>
          </div>
          <Button variant="outline" onClick={onClose} data-testid="dismiss-explore-btn">
            Dismiss
          </Button>
        </div>
      </Card>
    </div>
  );
}
